import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import styles from "./Settings.module.css";

interface ApiKey {
  id: string;
  key_prefix: string;
  name: string;
  created_at: number;
  last_used_at: number | null;
  revoked: number;
}

export default function Settings() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [extensionVersion, setExtensionVersion] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/extension/version")
      .then((r) => (r.ok ? (r.json() as Promise<{ version: string }>) : null))
      .then((data) => {
        if (data?.version) setExtensionVersion(data.version);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(() => {
    fetch("/api/api-keys")
      .then((r) => r.json() as Promise<ApiKey[]>)
      .then(setKeys)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function generate() {
    fetch("/api/api-keys", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: "Claude Code" }),
    })
      .then(
        (r) =>
          r.json() as Promise<{
            id: string;
            key: string;
            prefix: string;
            name: string;
          }>,
      )
      .then((data) => {
        setNewKey(data.key);
        setCopied(false);
        load();
      })
      .catch(() => {});
  }

  function revoke(id: string) {
    if (
      !confirm(
        "Revoke this API key? Any Claude Code session using it will stop working.",
      )
    )
      return;
    fetch(`/api/api-keys/${id}`, { method: "DELETE" })
      .then(() => load())
      .catch(() => {});
  }

  function copyKey() {
    if (!newKey) return;
    navigator.clipboard
      .writeText(newKey)
      .then(() => setCopied(true))
      .catch(() => {});
  }

  const activeKeys = keys.filter((k) => !k.revoked);
  const revokedKeys = keys.filter((k) => k.revoked);

  return (
    <div className={styles.page}>
      <div className={styles.topNav}>
        <Link to="/" className={styles.backBtn}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10 3L5 8l5 5"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Home
        </Link>
      </div>

      {newKey && (
        <div className={styles.keyReveal}>
          <div className={styles.keyRevealHeader}>
            <span className={styles.keyRevealTitle}>Your new API key</span>
            <button
              className={styles.keyRevealClose}
              onClick={() => setNewKey(null)}
            >
              Dismiss
            </button>
          </div>
          <p className={styles.keyRevealWarning}>
            Copy this key now. It won't be shown again.
          </p>
          <div className={styles.keyRevealValue}>
            <code className={styles.keyCode}>{newKey}</code>
            <button className={styles.copyBtn} onClick={copyKey}>
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className={styles.keyRevealConfig}>
            <span className={styles.configLabel}>Claude Code MCP config:</span>
            <pre className={styles.configBlock}>
              {JSON.stringify(
                {
                  "henry-intelligence": {
                    type: "streamableHttp",
                    url: "https://intelligence.kinghenry.au/mcp",
                    headers: { Authorization: `Bearer ${newKey}` },
                  },
                },
                null,
                2,
              )}
            </pre>
          </div>
        </div>
      )}

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>Chrome Extension</span>
          {extensionVersion && (
            <span className={styles.versionBadge}>v{extensionVersion}</span>
          )}
        </div>

        <div className={styles.card}>
          <div className={styles.cardTop}>
            <p className={styles.cardDesc}>
              The companion Chrome extension bridges Claude Code to HubSpot via
              your logged-in session. Pick whichever install path suits you.
            </p>
          </div>

          <div className={styles.installOptions}>
            <div className={styles.installOption}>
              <div className={styles.installOptionHeader}>
                <span className={styles.installOptionLabel}>Option A</span>
                <h3 className={styles.installOptionTitle}>Download the zip</h3>
                <span className={styles.installOptionMeta}>
                  Easiest. No git, no terminal.
                </span>
              </div>
              <a
                className={styles.generateBtn}
                href="/extension.zip"
                download="henry-intelligence-extension.zip"
              >
                Download .zip
              </a>
              <ol className={styles.installSteps}>
                <li>
                  Unzip into{" "}
                  <code>~/Documents/henry-intelligence-extension/</code>.
                </li>
                <li>
                  Open <code>chrome://extensions</code> and enable Developer
                  mode.
                </li>
                <li>
                  Click <strong>Load unpacked</strong> and select that folder.
                </li>
                <li>
                  To update: re-download from this page, overwrite the folder,
                  click the ↻ refresh icon on the extension card.
                </li>
              </ol>
            </div>

            <div className={styles.installOption}>
              <div className={styles.installOptionHeader}>
                <span className={styles.installOptionLabel}>Option B</span>
                <h3 className={styles.installOptionTitle}>Clone the repo</h3>
                <span className={styles.installOptionMeta}>
                  Update with <code>git pull</code>. Recommended for devs.
                </span>
              </div>
              <ol className={styles.installSteps}>
                <li>
                  <code>cd ~/Projects</code>
                </li>
                <li>
                  <code>
                    git clone
                    https://github.com/King-Henry-AU/henry-intelligence.git
                  </code>
                </li>
                <li>
                  <code>
                    cd henry-intelligence/chrome-extension && npm install && npm
                    run build
                  </code>
                </li>
                <li>
                  In <code>chrome://extensions</code>, click{" "}
                  <strong>Load unpacked</strong> and select{" "}
                  <code>
                    ~/Projects/henry-intelligence/chrome-extension/dist/
                  </code>
                  .
                </li>
                <li>
                  To update: <code>git pull && npm run build</code> in the repo,
                  then click ↻ on the extension card.
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionLabel}>API Keys</span>
        </div>

        <div className={styles.card}>
          <div className={styles.cardTop}>
            <p className={styles.cardDesc}>
              Generate a personal API key to connect Claude Code to Henry
              Intelligence. Each team member needs their own key.
            </p>
            <button className={styles.generateBtn} onClick={generate}>
              + Generate New Key
            </button>
          </div>

          {loading ? (
            <p className={styles.empty}>Loading...</p>
          ) : activeKeys.length === 0 ? (
            <p className={styles.empty}>No API keys yet.</p>
          ) : (
            <ul className={styles.list}>
              {activeKeys.map((k) => (
                <li key={k.id} className={styles.row}>
                  <div className={styles.rowMain}>
                    <span className={styles.keyName}>{k.name}</span>
                    <code className={styles.keyPrefix}>{k.key_prefix}...</code>
                  </div>
                  <div className={styles.rowMeta}>
                    <span className={styles.metaItem}>
                      Created{" "}
                      {new Date(k.created_at * 1000).toLocaleDateString()}
                    </span>
                    {k.last_used_at && (
                      <span className={styles.metaItem}>
                        Last used{" "}
                        {new Date(k.last_used_at * 1000).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <button
                    className={styles.revokeBtn}
                    onClick={() => revoke(k.id)}
                  >
                    Revoke
                  </button>
                </li>
              ))}
            </ul>
          )}

          {revokedKeys.length > 0 && (
            <div className={styles.revokedSection}>
              <span className={styles.revokedLabel}>Revoked</span>
              <ul className={styles.list}>
                {revokedKeys.map((k) => (
                  <li
                    key={k.id}
                    className={`${styles.row} ${styles.rowRevoked}`}
                  >
                    <div className={styles.rowMain}>
                      <span className={styles.keyName}>{k.name}</span>
                      <code className={styles.keyPrefix}>
                        {k.key_prefix}...
                      </code>
                    </div>
                    <span className={styles.revokedBadge}>Revoked</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
